import { useNavigate, useLocation } from "react-router-dom";
import { Headphones, Mail, Play, Phone, X } from "lucide-react";
import { useState, useEffect } from "react";
import { getData } from "../../services/Axios/Axios";
import { ApiUrl } from "../../config/apiConfig";

function LiveSupport() {
    const navigate = useNavigate();
    const location = useLocation();

    const [activeButton, setActiveButton] = useState("");
    const [videosWithDuration, setVideosWithDuration] = useState([]);
    const [selectedVideo, setSelectedVideo] = useState(null);

    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    };

    const getAllVideos = async () => {
        try {
            const response = await getData(ApiUrl.listVideos, { skip_number: 0 });
            const videoList = response.data;

            const videosWithDurations = await Promise.all(
                videoList.map((video) => {
                    return new Promise((resolve) => {
                        const vid = document.createElement("video");
                        vid.src = video.videoUrl;
                        vid.preload = "metadata";
                        vid.onloadedmetadata = () => {
                            const duration = formatDuration(vid.duration);
                            resolve({ ...video, duration });
                        };
                        vid.onerror = () => {
                            resolve({ ...video, duration: "00:00" });
                        };
                    });
                })
            );

            setVideosWithDuration(videosWithDurations);
        } catch (error) {
            console.error("Error fetching videos", error);
        }
    };

    useEffect(() => {
        if (location.pathname.includes("/myprofile")) {
            setActiveButton("myprofile");
        } else if (location.pathname.includes("/livesupport")) {
            setActiveButton("livesupport");
        } else {
            setActiveButton("");
        }
    }, [location.pathname]);

    useEffect(() => {
        getAllVideos();
    }, []);

    return (
        <div className="flex text-textColor bg-customGray font-sans min-h-screen">
            <div className="flex flex-col flex-1 w-4/5 p-0">
                {/* Header */}
                <div className="flex items-center bg-white px-4 py-3 shadow-sm space-x-6 border-b">
                    <div className="flex items-center space-x-2">
                        <Headphones size={20} color="#000000" />
                        <h3 className="font-bold text-lg">Live Support</h3>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 px-4 py-2">
                    {/* Left Column */}
                    <div className="space-y-4 col-span-2">
                        {/* Tabs */}
                        <div className="relative flex py-2 gap-2 bg-white px-2 rounded-lg">
                            <button
                                className={`px-2 py-1 text-sm lg:px-4 lg:py-1 rounded-md font-medium shadow-sm ${
                                    activeButton === "myprofile"
                                        ? "bg-greenBg text-white"
                                        : "bg-customGray hover:bg-greenBg hover:text-white"
                                }`}
                                onClick={() => navigate("/myprofile")}
                            >
                                My Profile
                            </button>
                            <button
                                className={`px-2 py-1 text-sm lg:px-4 lg:py-1 rounded-md font-medium shadow-sm ${
                                    activeButton === "livesupport"
                                        ? "bg-greenBg text-white"
                                        : "bg-customGray hover:bg-greenBg hover:text-white"
                                }`}
                                onClick={() => navigate("/livesupport")}
                            >
                                Live Support
                            </button>
                        </div>

                        {/* Contact Us */}
                        <div className="bg-greenBg text-white p-4 rounded-xl shadow-lg">
                            <h3 className="text-lg font-semibold mb-2">Contact Us</h3>
                            <p className="flex gap-6 items-center mb-1">
                                <Mail size={20} />
                                support@tms.com
                            </p>
                            <p className="flex gap-6 items-center">
                                <Phone size={20} />
                                +1 (209) 310 0333
                            </p>
                        </div>

                        {/* Submit Questions */}
                        <div className="bg-white p-4 rounded-xl shadow-lg space-y-2">
                            <h3 className="text-lg font-semibold">Submit Your Questions</h3>
                            <textarea
                                rows="3"
                                placeholder="Your Question"
                                className="w-full border hover:border-greenBg bg-customGray p-2 rounded-lg focus:outline-none focus:shadow-md"
                            ></textarea>
                            <textarea
                                rows="3"
                                placeholder="Best way to connect you"
                                className="w-full border hover:border-greenBg bg-customGray p-2 rounded-lg focus:outline-none focus:shadow-md"
                            ></textarea>
                            <button className="bg-customOrange text-white w-full py-1 px-4 rounded-lg hover:bg-greenBg">
                                Submit
                            </button>
                        </div>

                        {/* Watch Features */}
                        <div className="bg-white p-4 rounded-xl shadow-lg space-y-2">
                            <h3 className="text-lg font-semibold">Watch Features</h3>
                            <p>
                                Quick, informative clips on product features to enhance your TMS
                                experience.
                            </p>
                            <button className="w-full flex bg-customOrange text-white py-1 px-4 rounded-lg hover:bg-greenBg items-center justify-center gap-4">
                                <Play size={20} />
                                Watch Now
                            </button>
                        </div>

                        {/* Driver App */}
                        <div className="bg-white p-4 rounded-xl shadow-md space-y-3">
                            <h3 className="text-lg font-semibold">Driver App</h3>
                            <p>
                                Manage driver support tasks and streamline communication
                                effortlessly.
                            </p>
                            <button className="w-full bg-customOrange text-white py-1 px-4 rounded-lg hover:bg-greenBg">
                                Learn More
                            </button>
                        </div>
                    </div>

                    {/* Right Column - Videos */}
                    <div className="bg-white p-4 rounded-xl shadow-lg h-fit col-span-3">
                        <h3 className="text-lg font-semibold mb-4">Watch Video Tutorials</h3>
                        <ul className="space-y-3">
                            {videosWithDuration.map((video, index) => (
                                <li
                                    key={index}
                                    className="flex items-start gap-4 cursor-pointer hover:bg-gray-100 p-2 rounded-md"
                                    onClick={() => setSelectedVideo(video)}
                                >
                                    <div className="w-[14rem] h-[9rem] bg-gray-200 rounded-md overflow-hidden relative">
                                        <img
                                            src={video.thumbnail}
                                            alt={video.title}
                                            className="object-cover w-full h-full"
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                            <Play size={28} className="text-white" />
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-center">
                                        <p className="text-md font-medium">{video.title}</p>
                                        <p className="text-xs text-gray-500">{video.duration}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Fullscreen Video Modal */}
            {selectedVideo && (
                <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
                    <div className="absolute top-4 right-4">
                        <button
                            onClick={() => setSelectedVideo(null)}
                            className="text-white p-2 bg-gray-800 rounded-full hover:bg-red-600"
                        >
                            <X size={24} />
                        </button>
                    </div>
                    <div className="w-[90vw] h-[80vh] max-w-5xl bg-black rounded-lg overflow-hidden shadow-lg">
                        <video
                            src={selectedVideo.videoUrl}
                            controls
                            autoPlay
                            className="w-full h-full object-contain"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default LiveSupport;
